// A SNAP SCROLLER, as a gesture: a container parked on one snap slot, where dragging toward another
// slot IS the gesture. The drag and its momentum settle then run on the compositor with the browser's
// own fling physics, so nothing here drives motion — it only reports position back, which is the one
// thing the compositor and the main thread agree on.
//
// Ported from the `danchiano` app's `lib/snap-scroller.ts`, where the bottom sheet, the nav stack and
// the section pager all share it. The settle DETECTION is the part that is easy to get subtly wrong,
// and the two rules below (a held finger never settles; a mandatory-snap scroller may not rest between
// slots) were found there rather than here.

/**
 * How far a snap scroller has been dragged off its resting slot: `0` (at rest) → `1` (fully away).
 * Axis-agnostic — a vertical sheet passes `scrollTop`, the mobile menu `scrollLeft`.
 */
export function snapProgress(scrolled: number, maxScroll: number): number {
	if (maxScroll <= 0) return 0;
	const progress = 1 - scrolled / maxScroll;
	return progress < 0 ? 0 : progress > 1 ? 1 : progress;
}

/**
 * How far onto the empty slot a settle must land to count as gone. Mandatory snap only ever comes to
 * rest ON a slot, so this is not a threshold for the user's decision — the browser already made that
 * with its own fling physics. It absorbs a momentum tail that is still creeping when the settle fires.
 */
export const SNAP_LANDED = 0.98;

/** Nothing has been dismissed until a settle actually lands on the empty slot. */
export function settledDismissed(progress: number): boolean {
	return progress >= SNAP_LANDED;
}

/** How long after the last scroll event a sequence counts as settled, where `scrollend` is missing. */
export const SNAP_SETTLE_DEBOUNCE_MS = 140;

export interface SnapScrollerHandlers {
	/** The first frame of a scroll — where the surface switches into its dragging state. */
	onStart?: () => void;
	/** Every rAF while it moves. Reads plus cheap writes only; this is the frame path. */
	onFrame: () => void;
	/** The browser's momentum and snap have finished. `wasScrolling` is false for a settle nobody owned. */
	onSettle: (wasScrolling: boolean) => void;
}

/**
 * Watch `el` and report its gesture back, coalesced to one `onFrame` per animation frame — a starved
 * frame lags a derived cue rather than stalling the gesture, because the gesture is the browser's
 * scroll and not ours.
 */
export function observeSnapScroller(el: HTMLElement, handlers: SnapScrollerHandlers): { stop: () => void } {
	let frame = 0;
	let settleTimer = 0;
	let scrolling = false;
	/**
	 * A FINGER IS DOWN — and while one is, nothing settles. A mid-gesture pause is not a settle, and
	 * both signals will claim it is one: the debounce fires 140ms after the last scroll event, and a
	 * drag stalled under a stationary finger produces none; `scrollend` is specified to wait for the
	 * pointer, but a drag whose finger has left the scroller's box can end the scrolling operation
	 * with the touch still down.
	 *
	 * TOUCH events, not pointer ones: the browser fires `pointercancel` the instant a native scroll
	 * claims the gesture, while touch keeps firing for the gesture's whole life. The ends are watched
	 * on `window` so a release outside the scroller still lands.
	 */
	let held = false;
	/** A settle that arrived under a held finger. Owed to the release, which re-arms the debounce. */
	let owed = false;
	/**
	 * SNAP IS OFF for the rest of this touch. A settle arriving under a held finger is the browser
	 * announcing what it does next: having decided the scrolling operation is over, a mandatory-snap
	 * scroller may not rest between slots, so it animates to the nearest one — under the finger, which
	 * yanks it back, and the surface oscillates.
	 *
	 * Restoring it on release is itself the snap-back: restyling a snap scroller makes the browser
	 * re-evaluate and animate to the nearest slot, which after a hold (release velocity ~0) is exactly
	 * the settle the gesture wants.
	 */
	let snapSuppressed = false;

	const suppressSnap = () => {
		if (snapSuppressed) return;
		snapSuppressed = true;
		el.style.scrollSnapType = 'none';
	};

	const restoreSnap = () => {
		if (!snapSuppressed) return;
		snapSuppressed = false;
		el.style.scrollSnapType = '';
	};

	const supportsScrollEnd = 'onscrollend' in window;

	const publish = () => {
		frame = 0;
		handlers.onFrame();
	};

	const armDebounce = () => {
		clearTimeout(settleTimer);
		settleTimer = window.setTimeout(settle, SNAP_SETTLE_DEBOUNCE_MS);
	};

	function settle() {
		clearTimeout(settleTimer);
		settleTimer = 0;
		if (held) {
			owed = true;
			suppressSnap();
			return;
		}
		owed = false;
		const was = scrolling;
		scrolling = false;
		handlers.onSettle(was);
	}

	const onScroll = () => {
		if (!scrolling) {
			scrolling = true;
			handlers.onStart?.();
		}
		if (!frame) frame = requestAnimationFrame(publish);
		// Re-armed on every frame while it is the deciding signal, so it can only fire once the
		// scroller has stopped moving — never mid-snap. `owed` puts it back in charge on a `scrollend`
		// engine, for the settle refused under a finger whose release produces no movement.
		if (!supportsScrollEnd || owed) armDebounce();
	};

	const onTouchStart = () => {
		held = true;
		owed = false;
		// A fresh gesture supersedes whatever the last one was still counting down to.
		clearTimeout(settleTimer);
		settleTimer = 0;
	};

	const onTouchEnd = (event: TouchEvent) => {
		if (event.touches.length > 0) return; // another finger is still down; the gesture isn't over
		if (!held) return;
		held = false;
		// Hand the snap back BEFORE the debounce is armed: the restyle starts the browser's own
		// snap-back, whose scroll events then keep re-arming that clock until it lands.
		restoreSnap();
		// The release is where momentum and snap BEGIN, so a timer armed here would read a
		// half-finished position. Only a settle we owe gets one — that is the case where the engine
		// already spent its `scrollend` under the finger.
		if (owed) armDebounce();
	};

	el.addEventListener('scroll', onScroll, { passive: true });
	el.addEventListener('touchstart', onTouchStart, { passive: true });
	window.addEventListener('touchend', onTouchEnd, { passive: true });
	window.addEventListener('touchcancel', onTouchEnd, { passive: true });
	if (supportsScrollEnd) el.addEventListener('scrollend', settle);

	return {
		stop: () => {
			if (frame) cancelAnimationFrame(frame);
			clearTimeout(settleTimer);
			restoreSnap();
			el.removeEventListener('scroll', onScroll);
			el.removeEventListener('touchstart', onTouchStart);
			window.removeEventListener('touchend', onTouchEnd);
			window.removeEventListener('touchcancel', onTouchEnd);
			if (supportsScrollEnd) el.removeEventListener('scrollend', settle);
		},
	};
}
